import { ref } from 'vue'
import type { Ref } from 'vue'
import type { PublicInvitation, AcceptInvitationPayload } from '@/types/team'
import type { User } from '@/types/auth'
import { fetchInvitationByToken as apiFetchByToken, acceptInvitation as apiAccept } from '@/api/invitations'

export type AcceptInvitationErrorState = 'not-found' | 'expired' | 'cancelled' | 'accepted' | null

const metadata = ref<PublicInvitation | null>(null)
const loading = ref(false)
const submitting = ref(false)
const errors = ref<Record<string, string[]>>({})
const errorState = ref<AcceptInvitationErrorState>(null)

function isValidationError(
  e: unknown,
): e is { response: { data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { data?: unknown } }).response !== null &&
    typeof (e as { response: { data: unknown } }).response.data === 'object'
  )
}

function isNotFoundError(
  e: unknown,
): e is { response: { status: number } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: { status?: unknown } }).response === 'object' &&
    (e as { response: { status: unknown } }).response.status === 404
  )
}

async function fetchByToken(token: string): Promise<void> {
  loading.value = true
  metadata.value = null
  errorState.value = null
  errors.value = {}
  try {
    const result = await apiFetchByToken(token)
    metadata.value = result
    if (result.status === 'expired') {
      errorState.value = 'expired'
    } else if (result.status === 'cancelled') {
      errorState.value = 'cancelled'
    } else if (result.status === 'accepted') {
      errorState.value = 'accepted'
    }
  } catch (e: unknown) {
    if (isNotFoundError(e)) {
      errorState.value = 'not-found'
    } else {
      errorState.value = 'not-found'
    }
  } finally {
    loading.value = false
  }
}

async function accept(token: string, payload: AcceptInvitationPayload): Promise<User | null> {
  submitting.value = true
  errors.value = {}
  try {
    const user = await apiAccept(token, payload)
    return user
  } catch (e: unknown) {
    if (isValidationError(e)) {
      errors.value = e.response.data.errors
    }
    return null
  } finally {
    submitting.value = false
  }
}

export function useAcceptInvitation(): {
  metadata: Ref<PublicInvitation | null>
  loading: Ref<boolean>
  submitting: Ref<boolean>
  errors: Ref<Record<string, string[]>>
  errorState: Ref<AcceptInvitationErrorState>
  fetchByToken: (token: string) => Promise<void>
  accept: (token: string, payload: AcceptInvitationPayload) => Promise<User | null>
} {
  return {
    metadata,
    loading,
    submitting,
    errors,
    errorState,
    fetchByToken,
    accept,
  }
}
